import {
  type AITranslationSettingsResponseDto,
  ApiForAiSettingsService,
  type ToggleStateRequestDto,
} from '@api'
import {
  attach,
  combine,
  createEffect,
  createEvent,
  createStore,
  sample,
} from 'effector'
import { matchPath } from 'react-router'

import { Pages } from '@/consts/routes.ts'
import { $creatorId } from '@/models/creators'
import { initializeApp } from '@/models/init.ts'
import { $currentPage } from '@/models/router.ts'
import { toast } from '@/models/toasts'
import { getGlossaryEntriesFx } from '@/modules/ai-features/models/glossary-settings.ts'
import { setTypoFrequencyFx } from '@/modules/ai-features/models/typo-settings.ts'
import { handleError } from '@/utils/error'

type ApiMethod = (params: {
  creatorId: number
  requestBody: ToggleStateRequestDto
}) => Promise<void>

const createAiSettingEffect = (apiMethod: ApiMethod, settingName: string) => {
  const effectFx = attach({
    source: {
      creatorId: $creatorId,
    },
    mapParams: (state: boolean, { creatorId }) => ({
      creatorId: creatorId!,
      requestBody: { state },
    }),
    effect: createEffect(apiMethod),
  })

  effectFx.doneData.watch(() => {
    toast.success(`${settingName} setting updated successfully`)
  })

  effectFx.failData.watch((error) =>
    handleError(error, 'Error updating AI setting ' + settingName),
  )

  return effectFx
}

export const updateConvertToLowerCaseFx = createAiSettingEffect(
  ApiForAiSettingsService.setConvertToLowerCase,
  'Convert to lowercase',
)

export const updateRemoveCommasFx = createAiSettingEffect(
  ApiForAiSettingsService.setRemoveCommas,
  'Remove commas',
)

export const updateRemovePointsFx = createAiSettingEffect(
  ApiForAiSettingsService.setRemovePoints,
  'Remove points',
)

export const updateRemoveExclamationMarkFx = createAiSettingEffect(
  ApiForAiSettingsService.setRemoveExclamationMark,
  'Remove exclamation mark',
)

export const updateRemoveQuestionMarkFx = createAiSettingEffect(
  ApiForAiSettingsService.setRemoveQuestionMark,
  'Remove question mark',
)

export const updateRemoveApostropheskFx = createAiSettingEffect(
  ApiForAiSettingsService.setRemoveApostrophes,
  'Remove apostrophes',
)

export const updateGenerateEmojiFx = createAiSettingEffect(
  ApiForAiSettingsService.setGenerateEmoji,
  'Generate emoji',
)

export const updateIntroduceTyposFx = createAiSettingEffect(
  ApiForAiSettingsService.setIntroduceTypos,
  'Introduce typos',
)

export const getAiSettingsFx = attach({
  name: 'get Ai Settings',
  source: {
    creatorId: $creatorId,
  },
  mapParams: (_: void, { creatorId }) => ({
    creatorId: creatorId!,
  }),
  effect: createEffect(ApiForAiSettingsService.getAiTranslationSettings),
})

getAiSettingsFx.failData.watch((error) =>
  handleError(error, 'Error getting AI settings', getAiSettingsFx),
)

export const updateAiSettings = createEvent<AITranslationSettingsResponseDto>()

export const $aiSettings = createStore<AITranslationSettingsResponseDto | null>(
  null,
)
  .on(getAiSettingsFx.doneData, (_, settings) => settings)
  .on(updateAiSettings, (_, settings) => settings)

export const $textSettingsLoading = combine(
  updateConvertToLowerCaseFx.pending,
  updateRemoveCommasFx.pending,
  updateRemovePointsFx.pending,
  updateRemoveExclamationMarkFx.pending,
  updateRemoveQuestionMarkFx.pending,
  updateRemoveApostropheskFx.pending,
  updateGenerateEmojiFx.pending,
  (...states) => states.some(Boolean),
)

sample({
  clock: [
    updateConvertToLowerCaseFx.doneData,
    updateRemoveCommasFx.doneData,
    updateRemovePointsFx.doneData,
    updateRemoveExclamationMarkFx.doneData,
    updateRemoveQuestionMarkFx.doneData,
    updateRemoveApostropheskFx.doneData,
    updateGenerateEmojiFx.doneData,
    updateIntroduceTyposFx.doneData,
    setTypoFrequencyFx.doneData,
  ],
  target: getAiSettingsFx,
})

sample({
  clock: [initializeApp, $currentPage],
  source: { path: $currentPage },
  filter: ({ path }) => {
    return !!matchPath(Pages.CREATOR_AI_SETTINGS, path)
  },
  target: [getAiSettingsFx, getGlossaryEntriesFx],
})
